import { ApiController } from '@/services/ApiController';
import { ApiRoutes } from '@/enums/api/ApiRoutes';
import { AxiosResponse } from 'axios';
import {
    DeliveryCreatePayload,
    DeliveryMeta,
    DeliveryUpdatePayload,
    ValidateDeliveryPayload,
} from '@/modules/Logistics/interfaces/LogisticsInterfaces';

/**
 * DeliveryAPI - API controller for logistics deliveries/shipments
 * Handles all CRUD operations and data validation for delivery lists
 */
export default class DeliveryAPI {
    /**
     * Get all deliveries
     * GET /logistics/shipments
     */
    public static async getAll(): Promise<AxiosResponse> {
        try {
            const response = await ApiController.get(ApiRoutes.deliveries.getAll);
            return response as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Get a single delivery by ID
     * GET /logistics/shipments/{id}
     */
    public static async getById(id: string): Promise<AxiosResponse> {
        try {
            const response = await ApiController.get(ApiRoutes.deliveries.getById(id));
            return response as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Get delivery PDF
     * GET /logistics/shipments/{id}/pdf
     */
    public static async getPdf(id: string): Promise<AxiosResponse> {
        try {
            const response = await ApiController.get(ApiRoutes.deliveries.getPdf(id), null, true);
            return response as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Create a new delivery
     * POST /logistics/shipments
     * Sends data (parsed rows) and meta (form fields) at the same level
     */
    public static async create(payload: DeliveryCreatePayload): Promise<AxiosResponse> {
        const request = {
            data: payload.data,
            meta: payload.meta,
        };

        try {
            const response = await ApiController.post(ApiRoutes.deliveries.create, request);
            return response as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Update an existing delivery
     * PATCH /logistics/shipments/{id}
     */
    public static async update(id: string, payload: Partial<DeliveryMeta>): Promise<AxiosResponse> {
        const request = {
            data: {
                attributes: {
                    ...payload,
                },
                type: 'delivery_lists',
                id,
            },
        };

        try {
            const response = await ApiController.patch(ApiRoutes.deliveries.update(id), request);
            return response as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Delete a delivery
     * DELETE /logistics/shipments/{id}
     */
    public static async delete(id: string): Promise<AxiosResponse> {
        try {
            const response = await ApiController.delete(ApiRoutes.deliveries.delete(id));
            return response as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Validate delivery data on backend
     * POST /logistics/validate
     * Sends {data: ...} and returns {rows, errors, total, parsed, addresses, clients}
     */
    public static async validate(payload: ValidateDeliveryPayload): Promise<AxiosResponse> {
        const request = {
            data: payload.data,
        };

        try {
            const response = await ApiController.post(ApiRoutes.deliveries.validate, request);
            return response as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }
    }
}
